import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { formatDistanceToNowStrict } from "date-fns";
import { toast } from "sonner";
import {
  ArrowRight, BadgeCheck, Check, CheckCheck, ChevronDown, ClipboardList, Clock, Copy, FileText, MessageSquareWarning, Paperclip, PenLine, Play, Plus,
  RotateCcw, Send, ThumbsDown, ThumbsUp, Trash2, Undo2, Wallet, X,
} from "lucide-react";
import { Money } from "@/components/Money";
import { StatusPill } from "@/components/tasks/TaskBits";
import { ApprovalStatus } from "@/components/approvals/ApprovalBits";
import { CodeChip } from "@/components/approvals/ApprovalCode";
import { TINT, Priority } from "@/components/work/WorkBits";
import { TrashButton, clearHistory, deleteFeedEntry } from "@/components/work/SuperDelete";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export const EV = {
  created: [Plus, "sky"], endorsed: [Check, "emerald"], noted: [MessageSquareWarning, "rose"], revised: [PenLine, "violet"], forwarded: [Send, "indigo"],
  in_progress: [Play, "sky"], done: [CheckCheck, "emerald"], cancelled: [X, "rose"], submitted: [FileText, "teal"], recommended: [ThumbsUp, "teal"],
  returned: [Undo2, "amber"], resubmitted: [RotateCcw, "teal"], approved: [BadgeCheck, "emerald"], rejected: [ThumbsDown, "rose"], withdrawn: [X, "amber"],
};
const SHORT = { forwarded: "Forwarded", in_progress: "In progress", done: "Done", returned: "Returned" };
const ago = (at) => (at ? formatDistanceToNowStrict(new Date(at), { addSuffix: true }) : "");

/** One row open at a time on phones/tablets; several on desktop. */
export const useAccordion = () => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((s) => {
    const n = window.matchMedia("(max-width: 1023px)").matches ? new Set() : new Set(s);
    if (s.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  return [open, toggle];
};

export const TypeChip = ({ type, label, testid }) => {
  const [Icon, tint] = EV[type] || [FileText, "indigo"];
  return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold", TINT[tint].bg, TINT[tint].icon)} data-testid={testid}><Icon className="h-3 w-3" strokeWidth={2.6} />{SHORT[type] || label}</span>;
};

/** Accordion card: coloured accent bar, type chip, title, date · relative · actor, status/amount on the right, details below. */
export const ActivityRow = ({ type, label, title, at, by, pill, amount, code, company, open, onToggle, to, side, children, testid, press, badge, count, statusLabel }) => {
  const reduce = useReducedMotion();
  const tint = (EV[type] || [null, "indigo"])[1];
  const pid = `${testid}-panel`;
  return (
    <div className="act-card relative overflow-hidden rounded-2xl bg-white shadow-[0_2px_10px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/70" data-testid={testid} {...press}>
      <span className={cn("absolute inset-y-0 left-0 w-1.5", TINT[tint].solid)} aria-hidden="true" />
      <div className="flex items-stretch">
        <button type="button" aria-expanded={open} aria-controls={pid} onClick={onToggle} data-testid={`${testid}-toggle`}
          className="flex min-h-[64px] min-w-0 flex-1 items-start gap-2 py-3 pl-4 pr-2 text-left transition-colors hover:bg-slate-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">{badge}{(!statusLabel || (SHORT[type] || label || "").toLowerCase() !== statusLabel.toLowerCase()) && <TypeChip type={type} label={label} testid={`${testid}-type`} />}{pill}{company && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">{company}</span>}</div>
            <div className="break-words text-[14px] font-semibold leading-snug text-slate-900" data-blur>{title}</div>
            <div className="flex flex-wrap gap-x-1.5 text-[11.5px] text-slate-500" data-testid={`${testid}-meta`}>
              <span className="tabular-nums">{fmtDateTime(at).replace(" GST", "")}</span><span>· {ago(at)}</span>{by && <span className="font-medium text-slate-600" data-blur>· {by}</span>}
              {count > 0 && <span className="font-semibold text-teal-700" data-testid={`${testid}-count`}>· {count} {count === 1 ? "update" : "updates"}</span>}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {amount !== undefined && <span className="text-sm font-bold tabular-nums text-slate-900" data-testid={`${testid}-amount`}><Money value={amount} /></span>}
            {code && <CodeChip code={code} testid={`${testid}-code`} />}
            <ChevronDown className={cn("mt-auto h-5 w-5 text-slate-400 transition-transform duration-200 motion-reduce:transition-none", open && "rotate-180 text-teal-600")} />
          </div>
        </button>
        {side}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div id={pid} key="panel" className="overflow-hidden" initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }} exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }} transition={{ duration: reduce ? 0.01 : 0.24, ease: [0.2, 0.8, 0.2, 1] }}>
            <div className="border-t border-slate-100 bg-slate-50/50 py-3 pl-4 pr-3 sm:pr-4">
              {children}
              {to && <div className="mt-3 flex justify-end">
                <Link to={to} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2" data-testid={`${testid}-open`}>Open <ArrowRight className="h-4 w-4" /></Link>
              </div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Field = ({ label, children, wide }) => (
  <div className={cn("min-w-0", wide && "col-span-full")}>
    <dt className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
    <dd className="mt-0.5 break-words text-[13px] text-slate-800">{children || "—"}</dd>
  </div>
);
const Grid = ({ wide, children }) => <dl className={cn("grid grid-cols-2 gap-x-4 gap-y-3", wide && "lg:grid-cols-3")}>{children}</dl>;
const Note = ({ tone = "amber", title, e }) => (
  <div className={cn("col-span-full rounded-xl px-3 py-2 text-[13px]", tone === "rose" ? "bg-rose-50 text-rose-900" : "bg-amber-50 text-amber-900")}>
    <div className="text-[11px] font-semibold"><span data-blur>{title}{e.by && ` · ${e.by}`}</span>{e.at && ` · ${fmtDate(e.at)}`}</div>
    <div className="mt-0.5 whitespace-pre-wrap break-words" data-blur>{e.text}</div>
  </div>
);
const STATE = { endorsed: ["Endorsed", "text-emerald-700 bg-emerald-50"], noted: ["Noted", "text-orange-700 bg-orange-50"], pending: ["Pending", "text-slate-500 bg-white ring-1 ring-slate-200"] };

const Heads = ({ reviewers }) => (
  <ul className="mt-1 flex flex-wrap gap-1.5">
    {reviewers.map((r) => { const [l, c] = STATE[r.state]; return <li key={r.id} className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium", c)} data-blur>{r.state === "pending" ? <Clock className="h-3 w-3" /> : <Check className="h-3 w-3" />}{r.name} · {l}{r.at && ` ${fmtDate(r.at)}`}</li>; })}
  </ul>
);
const headsLabel = (t) => `Division Heads (${t.reviewers.filter((r) => r.state === "endorsed").length}/${t.reviewers.length} endorsed)`;

export const TaskDetails = ({ t, wide }) => (
  <Grid wide={wide}>
    {t.summary && <Field label="Description" wide><span className="line-clamp-4 whitespace-pre-wrap" data-blur>{t.summary}</span></Field>}
    <Field label="Status"><StatusPill t={t} /></Field>
    <Field label="Priority"><Priority p={t.priority} /></Field>
    <Field label="Due date"><span className={cn(t.overdue && "font-semibold text-rose-700")}>{fmtDate(t.due_date)}{t.overdue && " · Overdue"}</span></Field>
    <Field label="Created by"><span data-blur>{t.created_by.name}</span></Field>
    <Field label="Manager"><span data-blur>{t.assignee.name}</span></Field>
    <Field label="Last update">{fmtDateTime(t.updated_at)}{t.last_event && <span className="block text-[11.5px] text-slate-500" data-blur>{t.last_event.label} · {t.last_event.by}</span>}</Field>
    <Field label={headsLabel(t)} wide><Heads reviewers={t.reviewers} /></Field>
    {t.last_note && <Note title={`Latest note · ${t.last_note.label}`} e={t.last_note} />}
  </Grid>
);

const copy = (code) => navigator.clipboard.writeText(code).then(() => toast.success("Approval code copied")).catch(() => toast.error("Couldn't copy the code"));

export const RequestDetails = ({ r, wide }) => {
  const reason = ["returned", "rejected"].includes(r.status) && [...(r.steps || [])].reverse().find((s) => s.type === r.status && s.text);
  return (
    <Grid wide={wide}>
      {r.summary && <Field label="Description" wide><span className="line-clamp-4 whitespace-pre-wrap" data-blur>{r.summary}</span></Field>}
      {reason && <Note tone={r.status === "rejected" ? "rose" : "amber"} title={r.status === "rejected" ? "Rejection reason" : "Return reason"} e={reason} />}
      <Field label="Category">{r.category}</Field>
      <Field label="Amount"><span className="font-semibold"><Money value={r.amount} /></span></Field>
      <Field label="Payee"><span data-blur>{r.payee}</span></Field>
      <Field label="Needed by">{fmtDate(r.needed_by)}</Field>
      <Field label="Applicant"><span data-blur>{r.applicant.name}{r.applicant.role && ` · ${r.applicant.role}`}</span></Field>
      <Field label="Recommender"><span data-blur>{r.recommender?.name || "Not needed"}</span></Field>
      <Field label="MD decision"><span data-blur>{r.decision ? `${r.decision.by} · ${fmtDate(r.decision.at)}` : "Pending"}</span></Field>
      <Field label="Attachments">
        <Link to={`/approvals/${r.id}#attachments`} className="inline-flex items-center gap-1 font-medium text-teal-700 hover:underline" data-testid={`request-attachments-${r.id}`}><Paperclip className="h-3.5 w-3.5" />{r.attachments_count} · View</Link>
      </Field>
      {r.approval_code && (
        <Field label="Approval code"><CodeCopy code={r.approval_code} testid={`request-code-copy-${r.id}`} /></Field>
      )}
      {r.steps?.length > 0 && (
        <Field label="Steps" wide>
          <ol className="mt-1 space-y-2 border-l-2 border-slate-200 pl-3">
            {r.steps.map((s, i) => (
              <li key={i} className="relative">
                <span className={cn("absolute -left-[17px] top-1.5 h-2 w-2 rounded-full", TINT[(EV[s.type] || [null, "indigo"])[1]].solid)} />
                <div className="text-[12.5px]"><span className="font-semibold">{s.label}</span><span className="text-slate-500" data-blur> · {s.by} · {fmtDateTime(s.at).replace(" GST", "")}</span></div>
                {s.text && <div className="text-[12px] text-slate-600" data-blur>“{s.text}”</div>}
              </li>
            ))}
          </ol>
        </Field>
      )}
    </Grid>
  );
};

const KIND = { task: ["Task", ClipboardList, "bg-indigo-600"], approval: ["Approval", Wallet, "bg-pink-600"] };
const KindBadge = ({ kind, testid }) => {
  const [l, Icon, c] = KIND[kind];
  return <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm", c)} data-testid={testid}><Icon className="h-3 w-3" strokeWidth={2.6} />{l}</span>;
};

/** Key info shown above the tracking history. */
const ItemSummary = ({ x }) => {
  const it = x.item;
  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200" data-testid={`feed-summary-${x.id}`}>
      {x.kind === "task" ? (
        <Grid>
          <Field label="Manager"><span data-blur>{it.assignee.name}</span></Field>
          <Field label="Due date"><span className={cn(it.overdue && "font-semibold text-rose-700")}>{fmtDate(it.due_date)}{it.overdue && " · Overdue"}</span> <Priority p={it.priority} /></Field>
          <Field label={headsLabel(it)} wide><Heads reviewers={it.reviewers} /></Field>
        </Grid>
      ) : (
        <Grid>
          <Field label="Category">{it.category}</Field>
          <Field label="Applicant"><span data-blur>{it.applicant.name}{it.applicant.role && ` · ${it.applicant.role}`}</span></Field>
          <Field label="Recommender"><span data-blur>{it.recommender?.name || "Not needed"}</span></Field>
          <Field label="MD"><span data-blur>{it.decision ? `${it.decision.by} · ${fmtDate(it.decision.at)}` : "Pending"}</span></Field>
          <Field label="Attachments">
            <Link to={`/approvals/${it.id}#attachments`} className="inline-flex items-center gap-1 font-medium text-teal-700 hover:underline" data-testid={`feed-attachments-${x.id}`}><Paperclip className="h-3.5 w-3.5" />{it.attachments_count} · View</Link>
          </Field>
          {it.approval_code && <Field label="Approval code"><CodeCopy code={it.approval_code} testid={`feed-code-copy-${x.id}`} /></Field>}
        </Grid>
      )}
    </div>
  );
};

/** Vertical tracking timeline, oldest → newest, latest highlighted, pending next step as a dashed node. */
const Track = ({ x, canDelete, onDeleted }) => {
  const kind = x.kind === "task" ? "tasks" : "approvals";
  const n = x.events.length;
  return (
    <ol className="relative" data-testid={`feed-track-${x.id}`}>
      {x.events.map((e, idx) => {
        const [Icon, tint] = EV[e.type] || [FileText, "indigo"];
        const last = idx === n - 1;
        const del = canDelete ? async () => { if (await deleteFeedEntry({ label: e.label, title: x.title, ref: { kind, id: x.rid, i: e.i, at: e.at } })) onDeleted?.(); } : null;
        return (
          <li key={`${e.i}-${e.at}`} className="relative flex gap-3 pb-3 last:pb-0" data-testid={`feed-step-${x.id}-${e.i}`}>
            {(!last || x.next) && <span className="absolute bottom-0 left-[13px] top-7 w-0.5 bg-slate-200" aria-hidden="true" />}
            <span className={cn("relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white shadow-sm", TINT[tint].solid, last && "ring-4 ring-teal-100")}><Icon className="h-3.5 w-3.5" strokeWidth={2.6} /></span>
            <div className={cn("min-w-0 flex-1", last && "rounded-xl bg-white p-2.5 shadow-sm ring-1 ring-teal-200")}>
              <div className="flex items-start gap-1">
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-slate-900">{e.label}{last && <span className="rounded-full bg-teal-600 px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide text-white">Latest</span>}</div>
                  <div className="text-[11.5px] text-slate-500"><span className="font-medium text-slate-600" data-blur>{e.by}</span> · <span className="tabular-nums">{fmtDateTime(e.at).replace(" GST", "")}</span></div>
                </div>
                {del && <TrashButton onClick={del} testid={`feed-step-delete-${x.id}-${e.i}`} />}
              </div>
              {e.code && <div className="mt-1"><CodeCopy code={e.code} testid={`feed-step-code-${x.id}-${e.i}`} /></div>}
              {e.text && <p className={cn("mt-1.5 whitespace-pre-wrap break-words rounded-lg px-2.5 py-1.5 text-[12.5px]", e.type === "rejected" ? "bg-rose-50 text-rose-900" : "bg-amber-50 text-amber-900")} data-blur>{e.text}</p>}
            </div>
          </li>
        );
      })}
      {x.next && (
        <li className="relative flex gap-3 pt-3" data-testid={`feed-next-${x.id}`}>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-white text-slate-400"><Clock className="h-3.5 w-3.5" /></span>
          <div className="min-w-0 flex-1 pt-1 text-[12.5px] italic text-slate-400" data-blur>{x.next}</div>
        </li>
      )}
    </ol>
  );
};

/** One card per task / request: latest activity in the header, key info + full tracking history inside. */
export const FeedItem = ({ x, multi, canDelete, onDeleted, open, onToggle }) => {
  const it = x.item;
  const clear = async () => { if (await clearHistory(x.kind === "task" ? "tasks" : "approvals", x.rid, x.title)) onDeleted?.(); };
  return (
    <ActivityRow testid={`feed-item-${x.kind}-${x.rid}`} badge={<KindBadge kind={x.kind} testid={`feed-kind-${x.id}`} />} type={x.latest.type} label={x.latest.label} title={x.title}
      at={x.at} by={x.latest.by} count={x.count} open={open} onToggle={onToggle} to={x.url} company={multi ? x.company : null} statusLabel={it.status_label}
      amount={x.kind === "approval" ? it.amount : undefined} code={x.kind === "approval" && it.status === "approved" ? it.approval_code : null}
      pill={x.kind === "task" ? <StatusPill t={it} /> : <ApprovalStatus r={it} />}>
      <div className="space-y-3">
        <ItemSummary x={x} />
        <div>
          <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Tracking history</div>
          <Track x={x} canDelete={canDelete} onDeleted={onDeleted} />
        </div>
        {canDelete && <button type="button" onClick={clear} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50" data-testid={`feed-clear-${x.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete whole history</button>}
      </div>
    </ActivityRow>
  );
};

const CodeCopy = ({ code, testid }) => (
  <span className="inline-flex items-center gap-1.5"><CodeChip code={code} />
    <button type="button" onClick={() => copy(code)} aria-label="Copy approval code" className="inline-flex h-8 w-8 items-center justify-center rounded-full text-emerald-700 hover:bg-emerald-100" data-testid={testid}><Copy className="h-3.5 w-3.5" /></button>
  </span>
);

/** Detail-page timeline: every entry is an expandable row; super admins can delete single entries (audited). */
export const EventTimeline = ({ events, kind, rid, canDelete, onDeleted, testid }) => {
  const [open, toggle] = useAccordion();
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {events.map((e, i) => {
        const del = canDelete ? async () => { if (await deleteFeedEntry({ label: e.label, title: fmtDateTime(e.at), ref: { kind, id: rid, i, at: e.at } })) onDeleted?.(); } : null;
        const title = e.text ? `“${e.text.length > 90 ? `${e.text.slice(0, 90)}…` : e.text}”` : e.label;
        return (
          <ActivityRow key={`${i}-${e.at}`} testid={`${testid}-event-${i}`} type={e.type} label={e.label} title={title} at={e.at} by={e.by} code={e.code}
            open={open.has(i)} onToggle={() => toggle(i)}
            side={del && <div className="flex items-start pt-2"><TrashButton onClick={del} testid={`${testid}-delete-${i}`} /></div>}>
            <Grid wide>
              <Field label="Activity">{e.label}</Field>
              <Field label="By"><span data-blur>{e.by || "System"}</span></Field>
              <Field label="Date & time">{fmtDateTime(e.at)}</Field>
              {e.code && <Field label="Approval code"><CodeCopy code={e.code} testid={`${testid}-code-copy-${i}`} /></Field>}
              {e.text ? <Note title="Note" e={{ by: e.by, at: e.at, text: e.text }} /> : <Field label="Note" wide><span className="text-slate-400">No note was added</span></Field>}
            </Grid>
          </ActivityRow>
        );
      })}
    </div>
  );
};

const FILTERS = [["all", "All"], ["task", "Tasks"], ["approval", "Approvals"]];

/** "Recent activity" section: title, pulsing Live pill, count, type filter chips and the expandable feed. */
export const ActivityFeed = ({ feed, multi, canDelete, onDeleted }) => {
  const [kind, setKind] = useState("all");
  const [open, toggle] = useAccordion();
  const items = kind === "all" ? feed : feed.filter((e) => e.kind === kind);
  return (
    <section className="space-y-3" data-testid="work-feed">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[15px] font-semibold text-slate-900">Recent activity</h2>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200" data-testid="work-feed-live">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>Live
        </span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600" data-testid="work-feed-count">{items.length}</span>
      </div>
      <div className="flex gap-1.5" role="group" aria-label="Filter activity">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)} data-testid={`work-feed-filter-${k}`}
            className={cn("min-h-[36px] rounded-full px-3 text-xs font-semibold transition-colors", kind === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50")}>{l}</button>
        ))}
      </div>
      {items.length === 0 && <p className="py-6 text-center text-sm text-slate-500" data-testid="work-feed-empty">No activity yet</p>}
      <div className="space-y-2.5 lg:max-h-[75dvh] lg:overflow-y-auto lg:p-0.5 lg:pr-1">
        {items.map((x) => <FeedItem key={x.id} x={x} multi={multi} canDelete={canDelete} onDeleted={onDeleted} open={open.has(x.id)} onToggle={() => toggle(x.id)} />)}
      </div>
    </section>
  );
};
